import { getInstanceById } from 'echarts/core';
import { type EChartsExtensionInstallRegisters } from 'echarts';
import type ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';
import { queryDataIndex } from 'echarts/lib/util/model';
import Symbol from 'echarts/lib/chart/helper/Symbol';
import LineView from 'echarts/lib/chart/line/LineView';
import LineSeriesModel from 'echarts/types/src/chart/line/LineSeries';
import type GlobalModel from 'echarts/types/src/model/Global';
import type { Payload } from 'echarts/types/src/util/types';
import { SeriesData } from 'echarts/types/dist/shared';
import { LINE_WIDTH, MAIN_LINE_WIDTH } from '../../../theme/mobile/series/line';
import { Z_LIST } from '../../../theme/mobile/static/z';

// 通过改写LineView中highlight方法用到的SymbolClz类的构造方法，
// 完成高密度下主线和其他线的节点线宽的区分
const z = Z_LIST.lineSymbol;
class SymbolClz extends Symbol {
  constructor(
    data: SeriesData,
    idx: number,
    seriesScope?: SymbolDrawSeriesScope,
    opts?: SymbolOpts,
    isMainLine?: boolean
  ) {
    super(data, idx, seriesScope, opts);
    // 为节点添加高亮状态的样式
    this.childAt(0).states.emphasis = {
      style: {
        lineWidth: isMainLine ? MAIN_LINE_WIDTH : LINE_WIDTH,
        fill: '#fff'
      }
    };
    this.setZ(0, z);
  }
}

// 暂存echarts原生的highlight方法，在高亮整个系列的情况下执行原生方法。
const originHighlight = LineView.prototype.highlight;

// 修改之后的折线的高亮方法
// @ts-ignore
function highlight(
  seriesModel: LineSeriesModel,
  ecModel: GlobalModel,
  api: ExtensionAPI,
  payload: Payload
) {
  const data = seriesModel.getData();
  const dataIndex = queryDataIndex(data, payload);

  this._changePolyState('emphasis');

  if (!(dataIndex instanceof Array) && dataIndex != null && dataIndex >= 0) {
    const points = data.getLayout('points');
    let symbol = data.getItemGraphicEl(dataIndex) as SymbolClz;
    if (!symbol) {
      // Create a temporary symbol if it is not exists
      const x = points[dataIndex * 2];
      const y = points[dataIndex * 2 + 1];
      if (isNaN(x) || isNaN(y)) {
        // Null data
        return;
      }
      // fix #11360: should't draw symbol outside clipShapeForSymbol
      if (this._clipShapeForSymbol && !this._clipShapeForSymbol.contain(x, y)) {
        return;
      }
      const zlevel = seriesModel.get('zlevel') || 0;
      const isMainLine = seriesModel.option.id === 'mainLine';
      symbol = new SymbolClz(data, dataIndex, undefined, undefined, isMainLine);
      symbol.x = x;
      symbol.y = y;

      // ensure label text of the temporary symbol is in front of line and area polygon
      const symbolLabel = symbol.getSymbolPath().getTextContent();
      if (symbolLabel) {
        symbolLabel.zlevel = zlevel;
        symbolLabel.z = z;
        symbolLabel.z2 = this._polyline.z2 + 1;
      }

      (symbol as SymbolExtended).__temp = true;
      data.setItemGraphicEl(dataIndex, symbol);

      // Stop scale animation
      symbol.stopSymbolAnimation(true);

      this.group.add(symbol);
    }
    symbol.highlight();
  } else {
    // 高亮整个系列
    originHighlight.call(this, seriesModel, ecModel, api, payload);
  }
}

/**
 * 低密度折线图的symbol是实心的，高亮状态是空心的
 * 由于在配置中无法定义两种状态间图形的转化，通过zr直接修改图形
 * @param chart
 */
function parseLowDensityLineSymbol(chart: ExtensionAPI) {
  const lineSeriesList = chart.getModel().getSeriesByType('line');
  lineSeriesList.forEach(seriesModel => {
    const { option } = seriesModel;
    const ecIns = getInstanceById(chart.getId());
    const view = ecIns.getViewOfSeriesModel(seriesModel);

    if (option.showSymbol) {
      view.group.childAt(0).eachChild(child => {
        const zrIns = child.childAt(0);
        // 范式要求点的层级要比axisPointer的线高
        zrIns.z = Z_LIST.lineSymbol;
        const emphasisLineWidth = option.id === 'mainLine' ? MAIN_LINE_WIDTH : LINE_WIDTH;
        zrIns.style.lineWidth = emphasisLineWidth;
        zrIns.style.fill = seriesModel.option.itemStyle?.color;
        // 高亮态样式
        zrIns.states.emphasis = {
          style: {
            lineWidth: emphasisLineWidth,
            fill: option.dvEmphasisSymbolFill
          },
          shape: {
            width: zrIns.shape.width,
            height: zrIns.shape.height
          }
        };
        // 实心圆变成空心圆要求没有过渡效果
        if (zrIns.stateTransition) {
          zrIns.stateTransition.duration = 0;
        }
      });
    }
  });
}

function parseHighDensityLineSymbol(chart: ExtensionAPI) {
  // 此处为echarts原生的highlight源码，仅有极小部分的改动，已标注
  LineView.prototype.highlight = highlight;
}

function parseLineSymbol(chart: ExtensionAPI) {
  // 对高密度情况做处理
  parseHighDensityLineSymbol(chart);
  // 对低密度情况再做处理
  parseLowDensityLineSymbol(chart);
}

function parseLineLabelCount(model, api) {
  const lineOption = model.option;
  if (lineOption.dvMaxLabelCount !== undefined) {
    const lineView = api.getViewOfSeriesModel(model);
    const symbols = lineView.group._children[0]._children;
    if (symbols?.length > 0) {
      let existLabelCount = 0;
      symbols?.forEach(el => {
        const text = el._children[0]?._textContent?.style?.text;
        if (text?.length > 0) {
          existLabelCount++;
        }
      });
      symbols.forEach(el => {
        const textContent = el._children[0]?._textContent;
        if (existLabelCount > lineOption.dvMaxLabelCount) {
          textContent?.hide();
        } else {
          textContent?.show();
        }
      });
    }
  }
}

function parseBreathPoint(chart: ExtensionAPI) {
  const exhaleTime = 600;
  const inhaleTime = 900;
  const exhaleShadowBlur = 20;
  const inhaleShadowBlur = 10;
  const lineSeriesList = chart.getModel().getSeriesByType('line');
  lineSeriesList.forEach(seriesModel => {
    if (!seriesModel.option.dvShowBreathPoint) {
      return;
    }
    const ecIns = getInstanceById(chart.getId());
    const view = ecIns.getViewOfSeriesModel(seriesModel);

    const symbols = view.group.childAt(0)._children;
    if (symbols) {
      const zrIns = symbols[symbols.length - 1]?.childAt(0);
      const shadowColor = seriesModel.option.itemStyle?.color;
      if (zrIns && shadowColor !== undefined) {
        zrIns
          .animate('style', true)
          .when(exhaleTime, {
            shadowColor,
            shadowBlur: exhaleShadowBlur
          })
          .when(inhaleTime, {
            shadowColor,
            shadowBlur: inhaleShadowBlur
          })
          .start();

        zrIns.stateTransition.done = () => {
          if (zrIns.currentStates.includes('emphasis')) {
            zrIns
              .animate('style', true)
              .when(exhaleTime, {
                shadowColor,
                shadowBlur: exhaleShadowBlur
              })
              .when(inhaleTime, {
                shadowColor,
                shadowBlur: inhaleShadowBlur
              })
              .start();
          }
        };
      }
    }
  });
}

/**
 * 配置endLabel的z值层级关系
 * @param chart
 */
function parseEndLabelZIndex(chart: ExtensionAPI) {
  // 筛选出图表中所有的line系列的模型
  const lineSeriesList = chart.getModel().getSeriesByType('line');
  lineSeriesList.forEach(model => {
    // 获取每个line模型对应的视图层
    const view = chart.getViewOfSeriesModel(model);
    // 获取折线对应的endLabel数据模型
    const endLabelModel = model.getModel('endLabel');

    if (!endLabelModel || (endLabelModel && !endLabelModel.option.show)) {
      return;
    }
    // 获取配置项中的数据dvZ
    const { dvZ } = endLabelModel.option;
    // 从视图层修改endLabel的层级
    (view._endLabel as Element).attr({
      z: dvZ
    });
  });
}
/**
 *
 * @param registers
 */
export function lineSymbolExtensionInstall(registers: EChartsExtensionInstallRegisters) {
  registers.registerUpdateLifecycle('series:afterupdate', (_, chart) => {
    parseLineSymbol(chart);
    parseBreathPoint(chart);
    parseEndLabelZIndex(chart);
  });

  registers.registerUpdateLifecycle('series:afterupdate', (ecModel, api) => {
    ecModel.eachSeriesByType('line', model => {
      parseLineLabelCount(model, api);
    });
  });
}
